import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { getCallForwardingSettings, saveCallForwardingNumber } from '../../db/call-forwarding-resolvers.js';

type ForwardingStatus = {
  number: string;
  source: 'saved' | 'environment';
  canEdit: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
};
function parseStatus(raw: unknown): ForwardingStatus {
  const value = typeof raw === 'string' ? JSON.parse(raw) : null;
  if (!value || typeof value.number !== 'string' || typeof value.canEdit !== 'boolean')
    throw new Error('Weiterleitungsziel konnte nicht geladen werden.');
  return value;
}
export const CallForwardingPanel = stateful(() => {
  const [reload, setReload] = useState(0);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shown] = useState<{ value: ForwardingStatus | null }>({ value: null });
  const query = useQuery(() => getCallForwardingSettings()(), [reload]);
  useEffect(() => {
    const refresh = () => setReload(Date.now());
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);
  if (!query.loading && !query.error && query.value) {
    try {
      shown.value = parseStatus(query.value);
    } catch {
      shown.value = null;
    }
  }
  const status = shown.value;
  const save = async () => {
    const number = draft.trim().replace(/\s+/g, '');
    if (!/^\+[1-9]\d{6,14}$/.test(number)) {
      setError('Bitte eine internationale Rufnummer mit + und Ländervorwahl eingeben.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      shown.value = parseStatus(await saveCallForwardingNumber(number)());
      setEditing(false);
      setReload(Date.now());
    } catch {
      setError('Speichern fehlgeschlagen. Bitte neu laden und Berechtigung prüfen.');
    } finally {
      setSaving(false);
    }
  };
  return html`
    <style>
      .zw-forward-panel {
        box-sizing: border-box;
        width: 100%;
        max-width: 780px;
        margin: 0 0 24px;
        padding: 24px;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 3px 14px rgba(26, 34, 48, 0.04);
        color: #202630;
      }
      .zw-forward-panel .zw-forward-heading {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 18px;
      }
      .zw-forward-panel .zw-forward-icon {
        display: grid;
        place-items: center;
        width: 40px;
        height: 40px;
        flex: 0 0 40px;
        border-radius: 10px;
        color: #d6001c;
        background: #fff0f2;
      }
      .zw-forward-panel h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        line-height: 1.4;
      }
      .zw-forward-panel .zw-forward-subtitle,
      .zw-forward-panel .zw-forward-note {
        margin: 3px 0 0;
        color: #667085;
        font-size: 13px;
        line-height: 1.6;
      }
      .zw-forward-panel .zw-forward-current {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 16px;
      }
      .zw-forward-panel .zw-forward-label {
        margin: 0 0 5px;
        color: #667085;
        font-size: 12px;
        font-weight: 600;
      }
      .zw-forward-panel .zw-forward-number {
        margin: 0;
        font:
          600 23px/1.35 ui-monospace,
          SFMono-Regular,
          Consolas,
          monospace;
        letter-spacing: -0.03em;
        overflow-wrap: anywhere;
      }
      .zw-forward-panel .zw-forward-meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
        margin-top: 9px;
        color: #667085;
        font-size: 12px;
      }
      .zw-forward-panel .zw-forward-source {
        display: inline-flex;
        align-items: center;
        padding: 3px 8px;
        border: 1px solid #e3e7ed;
        border-radius: 6px;
        background: #f6f7f9;
        color: #4c5666;
        font-weight: 600;
      }
      .zw-forward-panel .zw-forward-note {
        margin-top: 18px;
        padding-top: 14px;
        border-top: 1px solid #edf0f4;
      }
      .zw-forward-panel .zw-forward-edit {
        display: flex;
        align-items: end;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 18px;
      }
      .zw-forward-panel .zw-forward-field {
        flex: 1 1 240px;
        margin: 0;
      }
      .zw-forward-panel label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.6;
      }
      .zw-forward-panel input {
        box-sizing: border-box;
        display: block;
        width: 100%;
        min-width: 0;
        height: 44px;
        margin-top: 6px;
        padding: 9px 12px;
        border: 1px solid #cbd2dc;
        border-radius: 8px;
        background: #fff;
        color: #202630;
        font:
          14px ui-monospace,
          SFMono-Regular,
          Consolas,
          monospace;
      }
      .zw-forward-panel button {
        box-sizing: border-box;
        min-height: 44px;
        padding: 9px 16px;
        border: 1px solid #d6001c;
        border-radius: 8px;
        background: #d6001c;
        color: #fff;
        font: inherit;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
      }
      .zw-forward-panel button:hover:not(:disabled) {
        background: #b80018;
        border-color: #b80018;
      }
      .zw-forward-panel .zw-forward-cancel {
        background: #fff;
        color: #445064;
        border-color: #d5dbe3;
      }
      .zw-forward-panel .zw-forward-cancel:hover:not(:disabled) {
        background: #f5f7fa;
        border-color: #b8c1ce;
      }
      .zw-forward-panel input:focus-visible,
      .zw-forward-panel button:focus-visible {
        outline: 3px solid #f4b9c2;
        outline-offset: 2px;
      }
      .zw-forward-panel :disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
      .zw-forward-panel .zw-forward-error {
        margin: 12px 0 0;
        color: #b00020;
        font-size: 13px;
        line-height: 1.5;
      }
      @media (max-width: 540px) {
        .zw-forward-panel {
          padding: 18px;
        }
        .zw-forward-panel .zw-forward-number {
          font-size: 21px;
        }
      }
    </style>
    <section class="zw-forward-panel" aria-label="Destination number">
      <div class="zw-forward-heading">
        <span class="zw-forward-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M14 5h6v6M20 5l-8 8M9 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-3" />
          </svg>
        </span>
        <div>
          <h3>Destination number</h3>
          <p class="zw-forward-subtitle">Zielnummer für die Übergabe an einen Kollegen</p>
        </div>
      </div>
      ${status
        ? html`
            <div class="zw-forward-current">
              <div>
                <p class="zw-forward-label">Aktueller Empfänger</p>
                <p class="zw-forward-number">${status.number}</p>
                <div class="zw-forward-meta">
                  <span class="zw-forward-source">
                    ${status.source === 'environment' ? 'Umgebungsstandard' : 'Individuell gespeichert'}
                  </span>
                  ${status.updatedAt
                    ? html`<span>Aktualisiert: ${new Date(status.updatedAt).toLocaleString('de-DE')}</span>`
                    : nothing}
                </div>
              </div>
              ${status.canEdit && !editing
                ? html`<button
                    type="button"
                    @click=${() => {
                      setDraft(status.number);
                      setEditing(true);
                      setError(null);
                    }}
                  >
                    Ändern
                  </button>`
                : nothing}
            </div>
            ${status.canEdit && editing
              ? html`<div class="zw-forward-edit">
                  <label class="zw-forward-field">
                    Neue Weiterleitungsnummer
                    <input
                      type="tel"
                      aria-label="Neue Weiterleitungsnummer"
                      autocomplete="tel"
                      placeholder="+49…"
                      .value=${draft}
                      ?disabled=${saving}
                      @input=${(event: Event) => setDraft((event.target as HTMLInputElement).value)}
                    />
                  </label>
                  <button type="button" ?disabled=${saving} @click=${save}>
                    ${saving ? 'Speichere…' : 'Speichern'}
                  </button>
                  <button
                    type="button"
                    class="zw-forward-cancel"
                    ?disabled=${saving}
                    @click=${() => {
                      setEditing(false);
                      setError(null);
                    }}
                  >
                    Abbrechen
                  </button>
                </div>`
              : nothing}
            <p class="zw-forward-note">
              Änderungen gelten für anschließend gestartete Weiterleitungen. Bereits gestartete Weiterleitungen behalten
              ihr Ziel.
            </p>
          `
        : html`<p class="zw-forward-subtitle">
            ${query.loading ? 'Lade Weiterleitungsziel…' : 'Weiterleitungsziel konnte nicht geladen werden.'}
          </p>`}
      ${query.error ? html`<p class="zw-forward-error" role="alert">Aktualisieren fehlgeschlagen.</p>` : nothing}
      ${error ? html`<p class="zw-forward-error" role="alert">${error}</p>` : nothing}
    </section>
  `;
});
